import { useQuery } from '@tanstack/react-query'
import { useEffect, useId, useRef } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/endpoints'
import { formatDate, formatNumber, intermediaryLabel } from '../format'
import { ErrorState, Loading } from './StateViews'

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** The side panel opened from a row of Movimenti (§8.2). */
export function IntermediaryPanel({
  number,
  onClose,
}: {
  number: string
  onClose: () => void
}) {
  const panelRef = useRef<HTMLElement>(null)
  const titleId = useId()

  // Read through a ref so the effect below can run once per opening: taking
  // `onClose` as a dependency would re-run it on every render of the page
  // behind, and the cleanup would steal the focus back each time.
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  })

  const detail = useQuery({
    queryKey: ['intermediary', number],
    queryFn: ({ signal }) => api.intermediary(number, signal),
  })

  // A side panel is a dialog, so it behaves like one: Escape closes it, Tab
  // stays inside it, and the focus goes back where it came from. Without this
  // the panel is a mouse-only feature and a keyboard user is left stranded
  // behind an overlay they cannot reach.
  useEffect(() => {
    const openedFrom = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const panel = panelRef.current
    panel?.focus()

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab' || !panel) return

      // Queried at every Tab, not once: the content arrives with the request.
      const stops = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (stops.length === 0) {
        event.preventDefault()
        panel.focus()
        return
      }
      const first = stops[0]
      const last = stops[stops.length - 1]
      const active = document.activeElement
      if (!panel.contains(active)) {
        event.preventDefault()
        ;(event.shiftKey ? last : first).focus()
      } else if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      openedFrom?.focus()
    }
  }, [])

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-slate-900/20"
      // Closing on mousedown on the backdrop itself, so that a selection
      // started inside the panel and released outside does not close it.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="h-full w-full max-w-md overflow-y-auto bg-white shadow-xl outline-none"
      >
        <header className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">{number}</p>
            <h2 id={titleId} className="text-base font-semibold">
              {detail.data ? intermediaryLabel(detail.data) : '…'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            aria-label="chiudi"
          >
            ✕
          </button>
        </header>

        {detail.isPending ? (
          <Loading />
        ) : detail.isError ? (
          <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
        ) : (
          <div className="space-y-5 px-5 py-4 text-sm">
            <dl className="grid grid-cols-2 gap-3">
              <Field label="Sezione" value={detail.data.section} />
              <Field label="Natura" value={detail.data.legal_form} />
              <Field label="Provincia" value={detail.data.province ?? '—'} />
              <Field label="Iscritto dal" value={formatDate(detail.data.registered_on)} />
              <Field
                label="Inoperativo"
                value={
                  detail.data.inactive
                    ? `sì, dal ${formatDate(detail.data.inactive_since)}`
                    : 'no'
                }
              />
              <Field label="Visto il" value={formatDate(detail.data.seen_on)} />
            </dl>

            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Mandati attivi ({formatNumber(detail.data.mandates.length)})
              </h3>
              {detail.data.mandates.length === 0 ? (
                <p className="text-slate-500">Nessun mandato attivo.</p>
              ) : (
                <ul className="space-y-1">
                  {detail.data.mandates.map((mandate) => (
                    <li key={mandate.company_code} className="flex justify-between gap-2">
                      <Link
                        to={`/companies/${mandate.company_code}`}
                        className="underline decoration-slate-300"
                      >
                        {mandate.company_name ?? mandate.company_code}
                      </Link>
                      <span className="whitespace-nowrap text-slate-500">
                        rilevato il {formatDate(mandate.appeared_on)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <Link
              to={`/intermediaries/${number}`}
              className="inline-block rounded-md bg-slate-900 px-3 py-1.5 text-sm text-white hover:bg-slate-800"
            >
              Apri la scheda completa
            </Link>
          </div>
        )}
      </aside>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  )
}
